import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AllocationOverview, Partner } from '../../api.js';
import { CREDITS_PATTERN, toCredits, toMillis } from '../../components/AllocationPanel.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';
import { PartnerAllocationTab } from '../partners/PartnerAllocationTab.js';

export function PartnerCreditsTab({ http, onToast }: { http: AdminHttpClient; onToast: (toast: ToastMessage) => void }) {
  const { t } = useTranslation();
  const [partners, setPartners] = useState<Partner[]>([]);
  const [partnerId, setPartnerId] = useState('');
  const [overview, setOverview] = useState<AllocationOverview>();
  const [credit, setCredit] = useState('');
  const [unlimited, setUnlimited] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async (id: string) => {
    if (!id) return setOverview(undefined);
    try {
      const next = await http.getPartnerAllocation(id);
      setOverview(next);
      setUnlimited(next.unlimited);
      setCredit(next.unlimited || next.credit.granted === undefined ? '' : String(toCredits(next.credit.granted)));
    } catch (error) { onToast({ tone: 'error', message: describeError(error, t) }); }
  };

  useEffect(() => { void http.listPartners().then(setPartners).catch((error) => onToast({ tone: 'error', message: describeError(error, t) })); }, [http, onToast, t]);

  function save(event: FormEvent) {
    event.preventDefault();
    const value = credit.trim();
    if (!partnerId || (!unlimited && value && !CREDITS_PATTERN.test(value))) return;
    setBusy(true);
    void http.setPartnerAllocation(partnerId, { unlimited, creditMillis: unlimited || !value ? undefined : toMillis(value) })
      .then(() => load(partnerId))
      .then(() => onToast({ tone: 'success', message: t('common.changed') }))
      .catch((error) => onToast({ tone: 'error', message: describeError(error, t) }))
      .finally(() => setBusy(false));
  }

  return <section className="content-stack"><label>{t('platformConsole.partner')}<select value={partnerId} onChange={(event) => { setPartnerId(event.target.value); void load(event.target.value); }}><option value="">{t('platformConsole.selectPartner')}</option>{partners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name}</option>)}</select></label>{partnerId && <PartnerAllocationTab allocation={overview} allocCredit={credit} allocUnlimited={unlimited} busy={busy} onCreditChange={setCredit} onUnlimitedChange={setUnlimited} onSave={save} />}{overview?.children.length ? <ul className="panel">{overview.children.map((child) => <li key={`${child.granteeKind}:${child.granteeId}`}>{child.name ?? child.granteeId}: {child.unlimited ? t('allocationPanel.unlimited') : toCredits(child.creditGranted ?? 0)}</li>)}</ul> : null}</section>;
}
